import React from 'react';
import { ChevronLeft, CheckCircle2, Star, Accessibility, PawPrint, Ban, Baby, Route, Landmark } from 'lucide-react';
import { TouristSite, TourQuestionnaire } from '../../../types';
import { cn } from '../../../lib/utils';

interface Step9Props {
  questionnaire: TourQuestionnaire;
  setQuestionnaire: React.Dispatch<React.SetStateAction<TourQuestionnaire>>;
  sites: TouristSite[];
  selectedCategory: string | null;
  setSelectedCategory: (category: string | null) => void;
  hideUnsuitable: boolean;
  setHideUnsuitable: (hide: boolean) => void;
  setSelectedActivity: (site: TouristSite) => void;
  setPreviousStep: (step: number) => void;
  setStep: (step: number) => void;
  handleBack: () => void;
  handleNext: () => void;
  CATEGORIES: { id: string; label: string }[];
  getSiteScore: (site: TouristSite) => { score: number; reasons: string[]; isUnsuitable: boolean; forcedColor: 'green' | 'yellow' | 'red' | 'orange' | null };
  getScoreColor: (color: 'green' | 'yellow' | 'red' | 'orange' | null) => string;
  getScoreBadge: (color: 'green' | 'yellow' | 'red' | 'orange' | null) => React.ReactNode;
  calculateLocalCost: (sites: TouristSite[], passengers: TourQuestionnaire['passengers']) => number;
}

export const Step9: React.FC<Step9Props> = ({
  questionnaire,
  setQuestionnaire,
  sites,
  selectedCategory,
  setSelectedCategory,
  hideUnsuitable,
  setHideUnsuitable,
  setSelectedActivity,
  setPreviousStep,
  setStep,
  handleBack,
  handleNext,
  CATEGORIES,
  getSiteScore,
  getScoreColor,
  getScoreBadge,
  calculateLocalCost
}) => {
  const calculateActivityPrice = (site: TouristSite) => {
    if (site.isFree) return 0;
    
    const adults = questionnaire.passengers.adults || 0;
    const children6_12 = questionnaire.passengers.children6_12 || 0;
    const children3_6 = questionnaire.passengers.children3_6 || 0;
    const babies = questionnaire.passengers.babies || 0;

    const priceAdult = site.adultPrice || 0;
    const priceChild6_12 = site.child6_12Price || 0;
    const priceChild3_6 = site.child3_6Price || 0;
    const priceBaby = site.babyPrice || 0;

    return (adults * priceAdult) + 
           (children6_12 * priceChild6_12) + 
           (children3_6 * priceChild3_6) + 
           (babies * priceBaby);
  };

  const filteredSites = sites
    .filter(s => {
      const siteSector = (s.sector || '').trim().toLowerCase().replace(/\s+/g, '');
      const selectedSector = (questionnaire.sector || '').trim().toLowerCase().replace(/\s+/g, '');
      const matchesSector = siteSector === selectedSector;
      
      // Exclude Restauration and Hébergement based strictly on categories and types
      const siteCategories = (s.categories || []).map(c => c.toLowerCase().trim());
      const isRestauration = siteCategories.includes('restauration') || s.cateringType === 'restaurant' || s.cateringType === 'snack';
      const isAccommodation = siteCategories.includes('hebergement') || s.isAccommodation === true;
      if (isRestauration || isAccommodation) return false;

      let matchesCategory = false;
      if (!selectedCategory) {
        matchesCategory = true;
      } else {
        const siteCategories = s.categories || [];
        matchesCategory = siteCategories.includes(selectedCategory);
      }
      
      if (hideUnsuitable) {
        const { isUnsuitable } = getSiteScore(s);
        if (isUnsuitable) return false;
      }

      return matchesSector && matchesCategory && s.isVisibleClient;
    })
    .sort((a, b) => {
      const scoreA = getSiteScore(a).score;
      const scoreB = getSiteScore(b).score;
      return scoreB - scoreA;
    });

  return (
    <div className="flex flex-col h-full p-0 overflow-hidden">
      <div className="shrink-0 px-4 py-3 space-y-3 bg-zinc-950/50 backdrop-blur-sm border-b border-white/5">
        <div className="flex items-center justify-between">
          <h2 className="text-lg md:text-xl font-bold text-white truncate">Activités : {questionnaire.sector}</h2>
          <button onClick={handleBack} className="p-1.5 text-zinc-400 hover:text-white transition-colors bg-zinc-900 rounded-lg border border-zinc-800">
            <ChevronLeft className="w-5 h-5" />
          </button>
        </div>

        {/* Category Filter */}
        <div className="space-y-3">
          <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar">
            <button
              onClick={() => setSelectedCategory(null)}
              className={cn("px-3 py-1.5 rounded-full text-[10px] font-bold whitespace-nowrap transition-all border", !selectedCategory ? 'bg-emerald-500 border-emerald-500 text-white' : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:border-zinc-700')}
            >
              Tous
            </button>
            {CATEGORIES.map(cat => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id === selectedCategory ? null : cat.id)}
                className={cn("px-3 py-1.5 rounded-full text-[10px] font-bold whitespace-nowrap transition-all border", cat.id === selectedCategory ? 'bg-emerald-500 border-emerald-500 text-white' : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:border-zinc-700')}
              >
                {cat.label}
              </button>
            ))}
          </div>

          <label className="flex items-center gap-2 cursor-pointer group">
            <div 
              onClick={() => setHideUnsuitable(!hideUnsuitable)}
              className={cn(
                "w-8 h-4 rounded-full p-0.5 transition-colors duration-200 ease-in-out",
                hideUnsuitable ? "bg-emerald-500" : "bg-zinc-700"
              )}
            >
              <div className={cn(
                "w-3 h-3 bg-white rounded-full transition-transform duration-200 ease-in-out",
                hideUnsuitable ? "translate-x-4" : "translate-x-0"
              )} />
            </div>
            <span className="text-[10px] font-bold text-zinc-500 group-hover:text-zinc-400 transition-colors">Masquer les lieux inadaptés</span>
          </label>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar px-3 pt-3 pb-32 overscroll-none">
        <div className="flex flex-col gap-2.5">
          {filteredSites.length > 0 ? filteredSites.map((site) => (
            <button
              key={site.id}
              onClick={() => {
                setSelectedActivity(site);
                setPreviousStep(9);
                setStep(10);
              }}
              className={cn(
                "w-full p-2 text-left transition-all flex items-center gap-3 rounded-xl border backdrop-blur-sm",
                getScoreColor(getSiteScore(site).forcedColor)
              )}
            >
              <div className="w-16 h-16 md:w-20 md:h-20 rounded-lg bg-zinc-800 overflow-hidden shrink-0 border border-white/10 relative">
                {site.image && site.image.length > 0 && !site.image.includes('maps.googleapis.com') ? (
                  <img src={site.image} alt={site.name} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-xl">📍</div>
                )}
                {questionnaire.selectedActivities.includes(site.id) && (
                  <div className="absolute top-0 left-0 w-8 h-8 bg-emerald-500 rounded-br-xl flex items-center justify-center shadow-lg z-10">
                    <CheckCircle2 className="w-5 h-5 text-white" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-start gap-2 mb-0.5">
                  <h3 className="font-bold text-white text-sm leading-tight truncate">{site.name}</h3>
                  {getScoreBadge(getSiteScore(site).forcedColor)}
                </div>
                <p className="text-[10px] text-zinc-400 line-clamp-2 leading-tight mb-1.5">{site.description}</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 text-amber-400">
                      <Star className="w-3 h-3 fill-current" />
                      <span className="text-[10px] font-bold">{site.rating || site.googleRating || "N/A"} {site.userRatingsTotal || site.googleReviewsCount ? `(${site.userRatingsTotal || site.googleReviewsCount} avis)` : ''}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {questionnaire.pmr && site.isHandicapAccessible && (
                      <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-500/20 border border-blue-500/30" title="Accessible PMR">
                        <Accessibility className="w-3.5 h-3.5 text-blue-400" />
                      </div>
                    )}
                    
                    {questionnaire.hasAnimal && site.dogsNotAllowed && (
                      <div className="relative flex items-center justify-center w-6 h-6 rounded-full bg-red-500/20 border border-red-500/30" title="Chiens interdits">
                        <PawPrint className="w-3.5 h-3.5 text-red-400" />
                        <Ban className="w-5 h-5 text-red-500 absolute opacity-50" strokeWidth={1} />
                      </div>
                    )}

                    {((questionnaire.passengers.children3_6 || 0) > 0 || (questionnaire.passengers.babies || 0) > 0) && site.notSuitableForChildren && (
                      <div className="relative flex items-center justify-center w-6 h-6 rounded-full bg-red-500/20 border border-red-500/30" title="Inadapté aux bébés">
                        <Baby className="w-3.5 h-3.5 text-red-400" />
                        <Ban className="w-5 h-5 text-red-500 absolute opacity-50" strokeWidth={1} />
                      </div>
                    )}

                    {questionnaire.motionSickness && site.isWindingRoad && (
                      <div className="flex items-center justify-center w-6 h-6 rounded-full bg-amber-500/20 border border-amber-500/30" title="Route sinueuse">
                        <Route className="w-3.5 h-3.5 text-amber-400" />
                      </div>
                    )}

                    <div className="flex items-center gap-1 text-emerald-400 font-bold text-[10px] ml-1">
                      {site.isFree ? "GRATUIT" : (
                        <span>{calculateActivityPrice(site)}€</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </button>
          )) : (
            <div className="p-10 text-center text-zinc-500 text-sm">
              Aucune activité trouvée pour ce secteur ou cette catégorie.
            </div>
          )}
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-zinc-950/95 backdrop-blur-xl border-t border-white/10 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex flex-col">
            <div className="flex items-center gap-1.5 text-zinc-400">
              <Landmark className="w-3 h-3 text-emerald-500" />
              <span className="text-[9px] font-bold uppercase tracking-wider">Total estimé</span>
            </div>
            <span className="text-lg font-bold text-emerald-400 leading-none">
              {calculateLocalCost(sites.filter(s => questionnaire.selectedActivities.includes(s.id)), questionnaire.passengers)} €
            </span>
          </div>
          
          <div className="flex items-center gap-2 flex-1 justify-end">
            <button 
              onClick={handleBack} 
              className="px-5 py-3 bg-zinc-800 text-zinc-300 text-xs rounded-xl hover:bg-zinc-700 transition-colors border border-zinc-700 font-semibold"
            >
              Retour
            </button>
            <button 
              onClick={handleNext}
              disabled={questionnaire.selectedActivities.length === 0}
              className="px-6 py-3 bg-emerald-500 text-white text-xs rounded-xl hover:bg-emerald-600 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_0_15px_rgba(16,185,129,0.3)]"
            >
              Calculer l'itinéraire
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
